/**
 * CarteScreen — vue client (pattern Sunday scroll-spy 2 niveaux).
 *
 * Tabs niveau 1 = roots (ex. "Côté boissons", "Côté cuisine"). Masqués s'il n'y
 * a qu'un seul root : sa pastille ferait doublon avec le titre « La Carte ».
 * Tabs niveau 2 = enfants directs du root actif (ex. "Vins rouges", "Bières").
 * Sections = une par tab niveau 2 ; chaque section liste tous les produits du
 * sous-arbre (DFS). Le scroll fait défiler les sections, un listener `scroll`
 * met à jour la tab niveau 2 active (scroll-spy), et la tab strip auto-centre
 * la tab active horizontalement.
 *
 * Note scroller : le conteneur scrollable est `.app-content` (HTML/body lockés
 * en `overflow: hidden` pour le pattern Capacitor). `scrollIntoView` ne walk pas
 * fiablement la chaîne pour trouver ce conteneur, donc on le ref explicitement
 * + on appelle `scroller.scrollTo({top})`. Le scroll-spy mesure lui aussi les
 * positions relativement à ce conteneur, pas au viewport.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './CarteScreen.css';
import { ProductDetailSheet, type ViewableProduct } from '../components/ProductDetailSheet';
import { MaskIcon } from '../components/Icon';
import { apiFetch } from '../utils/api';

const STICKY_OFFSET = 92; // fallback hauteur header+tabs sticky si mesure indispo (mesuré dynamiquement sinon)
const SCROLL_DURATION_MS = 380;
const BOTTOM_EPS = 8; // tolérance px pour considérer le scroll arrivé au fond (arrondis subpixel)

/**
 * Smooth scroll setTimeout-based — `scrollTo({behavior:'smooth'})` est cassé
 * sur `.app-content` (overflow: hidden auto dans un flex layout) : Chromium et
 * iOS WKWebView l'ignorent silencieusement (le `behavior:'auto'` marche, lui).
 * setTimeout au lieu de RAF pour rester fiable quand le tab est en background
 * (RAF est throttlé) — 60fps ≈ 16ms tick, easeOutQuad, durée 380ms.
 */
function smoothScrollTo(el: HTMLElement, top: number, duration = SCROLL_DURATION_MS) {
  const start = el.scrollTop;
  const delta = top - start;
  if (Math.abs(delta) < 1) return;
  const t0 = performance.now();
  function step() {
    const elapsed = performance.now() - t0;
    const t = Math.min(1, elapsed / duration);
    const eased = 1 - (1 - t) * (1 - t);
    el.scrollTop = start + delta * eased;
    if (t < 1) setTimeout(step, 16);
  }
  step();
}

type ProductEnriched = {
  cashpadId: string;
  photoUrl: string | null;
  description: string | null;
  allergenes: string[];
  badges: string[];
  updatedAt: number;
  updatedBy: string | null;
};

interface ResolvedProduct {
  id: string;
  name: string;
  price: number;
  type: 'liquid' | 'solid' | 'other';
  available: boolean;
  /** Contenants/formats (25cl/50cl, verre/bouteille) si l'article en a ≥2. */
  formats?: { id: string; label: string; price: number; available: boolean }[];
  sourceCategoryId: string;
  sourceProvider: string;
  enriched?: ProductEnriched | null;
}

interface ResolvedNode {
  id: string;
  parentId: string | null;
  label: string;
  emoji: string | null;
  description: string | null;
  position: number;
  hidden: boolean;
  children: ResolvedNode[];
  products: ResolvedProduct[];
}

interface UnmappedCategory {
  provider: string;
  categoryId: string;
  categoryName: string;
  productCount: number;
}

interface NavigationResponse {
  roots: ResolvedNode[];
  unmappedCategories: UnmappedCategory[];
}

const ALLERGEN_EMOJI: Record<string, string> = {
  gluten: '🌾', lactose: '🥛', oeuf: '🥚', soja: '🫘',
  'fruits à coque': '🌰', arachide: '🥜', poisson: '🐟',
  crustacés: '🦐', mollusques: '🐚', sésame: '', céleri: '🌿',
  moutarde: '', sulfites: '🍇', lupin: '',
};
const MAX_META_ICONS = 4;

/** DFS : tous les produits sous un sous-arbre, dans l'ordre. */
function collectProducts(node: ResolvedNode): ResolvedProduct[] {
  const out: ResolvedProduct[] = [...node.products];
  for (const child of node.children) {
    if (!child.hidden) out.push(...collectProducts(child));
  }
  return out;
}

const fmtEur = (n: number) => n.toFixed(2).replace('.', ',');

/** Icône placeholder (assets dispo : wine-glass + utensils) selon le type produit. */
function placeholderIcon(type: ResolvedProduct['type']): string {
  return type === 'solid' ? 'utensils' : 'wine-glass';
}

/**
 * Carte produit client enrichie : vignette 64×64 (photo ou placeholder duotone
 * selon le type), nom (2 lignes max) + prix accentué à droite, description
 * (2 lignes max), pills badges/allergènes, puis chips de format (25cl/50cl,
 * verre/bouteille) si l'article a ≥2 contenants. Le prix affiché suit le format
 * actif (défaut = 1er dispo) ; tap chip change le format (info pure, pas de
 * panier) ; format en rupture = chip grisée non cliquable. Tap carte (hors chip)
 * = ouvre la fiche détail.
 */
function CarteProductCard({
  p, nodeLabel, onOpen,
}: {
  p: ResolvedProduct;
  nodeLabel: string;
  onOpen: (v: { product: ResolvedProduct; nodeLabel: string }) => void;
}) {
  const formats = p.formats && p.formats.length >= 2 ? p.formats : null;
  const defaultIdx = formats ? Math.max(0, formats.findIndex((f) => f.available)) : 0;
  const [activeIdx, setActiveIdx] = React.useState(defaultIdx);
  const activeFormat = formats ? (formats[activeIdx] ?? formats[0]) : null;
  const displayPrice = activeFormat ? activeFormat.price : p.price;

  const photo = p.enriched?.photoUrl;
  const description = p.enriched?.description?.trim();
  const badges = p.enriched?.badges ?? [];
  const allergenes = p.enriched?.allergenes ?? [];
  const allergenIcons = allergenes
    .map((a) => ({ key: a, emoji: ALLERGEN_EMOJI[a] ?? '' }))
    .filter((x) => x.emoji.length > 0);
  const visibleBadges = badges.slice(0, MAX_META_ICONS);
  const remainingSlots = Math.max(0, MAX_META_ICONS - visibleBadges.length);
  const visibleAllergens = allergenIcons.slice(0, remainingSlots);
  const totalShown = visibleBadges.length + visibleAllergens.length;
  const hiddenCount = (badges.length + allergenIcons.length) - totalShown;
  const showMeta = !p.available || visibleBadges.length > 0 || visibleAllergens.length > 0 || hiddenCount > 0;

  return (
    <div
      role="button"
      tabIndex={0}
      className={`cic-card ${!p.available ? 'is-unavailable' : ''}`}
      onClick={() => onOpen({ product: p, nodeLabel })}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen({ product: p, nodeLabel }); } }}
    >
      <div className={`cic-thumb ${photo ? 'has-photo' : ''}`}>
        {photo
          ? <img src={photo} alt="" loading="lazy" />
          : <MaskIcon name={placeholderIcon(p.type)} size={26} color="#c19a6b" className="cic-thumb-icon" />}
      </div>

      <div className="cic-main">
        <div className="cic-head">
          <span className="cic-name">{p.name}</span>
          <span className="cic-price">{fmtEur(displayPrice)} €</span>
        </div>

        {description && <p className="cic-desc">{description}</p>}

        {showMeta && (
          <div className="cic-badges">
            {!p.available && <span className="cic-pill cic-pill--rupture">Rupture</span>}
            {visibleBadges.map((b) => (
              <span key={`b-${b}`} className="cic-pill">{b}</span>
            ))}
            {visibleAllergens.map((a) => (
              <span key={`a-${a.key}`} className="cic-allergen" title={a.key} aria-label={`allergène : ${a.key}`}>
                {a.emoji}
              </span>
            ))}
            {hiddenCount > 0 && <span className="cic-pill cic-pill--more">+{hiddenCount}</span>}
          </div>
        )}

        {formats && (
          <div className="cic-formats">
            {formats.map((f, i) => (
              <button
                key={f.id}
                type="button"
                className={`cic-chip ${i === activeIdx ? 'is-active' : ''} ${!f.available ? 'is-out' : ''}`}
                disabled={!f.available}
                onClick={(e) => { e.stopPropagation(); if (f.available) setActiveIdx(i); }}
              >
                {f.label} · {fmtEur(f.price)} €
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function CarteScreen() {
  const [data, setData] = useState<NavigationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [level1Id, setLevel1Id] = useState<string | null>(null);
  const [level2Id, setLevel2Id] = useState<string | null>(null);
  const [opened, setOpened] = useState<{ product: ResolvedProduct; nodeLabel: string } | null>(null);

  const [scrolled, setScrolled] = useState(false);

  const carteRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const topbarRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map());
  const tabL2StripRef = useRef<HTMLDivElement | null>(null);
  const tabL2Refs = useRef<Map<string, HTMLElement>>(new Map());

  // Offset sticky = hauteur réelle du topbar (titre + tabs + safe-area), mesurée
  // pour rester juste sur device (safe-area variable) comme en preview.
  const stickyOffset = () => topbarRef.current?.offsetHeight ?? STICKY_OFFSET;
  // Pendant 800ms après un tap N2, on ignore le scroll-spy pour éviter qu'un
  // état intermédiaire de scroll réécrive le level2Id voulu par le user.
  const userTappedUntilRef = useRef<number>(0);

  useEffect(() => {
    apiFetch(`/pos/navigation`, { auth: false })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<NavigationResponse>;
      })
      .then((d) => { setData(d); setLoading(false); })
      .catch((err) => { setError(err?.message ?? 'Erreur réseau'); setLoading(false); });
  }, []);

  // Localise le conteneur scrollable une fois monté.
  useEffect(() => {
    const el = carteRef.current?.closest('.app-content') as HTMLElement | null;
    scrollerRef.current = el ?? null;
  }, []);

  // Ombre du header sticky quand on a scrollé (pattern iOS). Listener always-on
  // (indépendant du scroll-spy qui ne tourne que si >1 sous-onglet).
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const onScroll = () => setScrolled(scroller.scrollTop > 4);
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [loading]);

  const visibleRoots = useMemo(
    () => (data?.roots ?? []).filter((r) => !r.hidden),
    [data],
  );

  const activeL1 = useMemo(() => {
    if (visibleRoots.length === 0) return null;
    return visibleRoots.find((r) => r.id === level1Id) ?? visibleRoots[0];
  }, [visibleRoots, level1Id]);

  const visibleL2 = useMemo(
    () => (activeL1?.children ?? []).filter((c) => !c.hidden),
    [activeL1],
  );

  // Default L2 quand on switche L1 (ou au premier render).
  useEffect(() => {
    if (visibleL2.length > 0) {
      // Si level2Id n'est pas dans la liste, le reset au premier enfant.
      const has = visibleL2.some((c) => c.id === level2Id);
      if (!has) setLevel2Id(visibleL2[0].id);
    } else {
      setLevel2Id(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeL1?.id]);

  const onPickLevel1 = useCallback((rootId: string) => {
    setLevel1Id(rootId);
    // Revient au top du conteneur — sections vont rerender pour ce root.
    const scroller = scrollerRef.current;
    if (scroller) smoothScrollTo(scroller, 0);
  }, []);

  const onPickLevel2 = useCallback((nodeId: string) => {
    setLevel2Id(nodeId);
    userTappedUntilRef.current = Date.now() + 800;
    const section = sectionRefs.current.get(nodeId);
    const scroller = scrollerRef.current;
    if (!section || !scroller) return;
    const sectionRect = section.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const targetTop = scroller.scrollTop + (sectionRect.top - scrollerRect.top) - stickyOffset();
    smoothScrollTo(scroller, Math.max(0, targetTop));
  }, []);

  // Scroll-spy : sur scroll, identifie la section "active" comme celle dont le
  // titre est le plus proche (et juste sous) la ligne des tabs sticky. Plus
  // simple et plus prévisible qu'IntersectionObserver pour ce layout.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || visibleL2.length <= 1) return; // collapsed → pas de scroll-spy L2
    const triggerY = stickyOffset() + 8;

    const compute = () => {
      if (Date.now() < userTappedUntilRef.current) return;
      // Fin de course : une dernière section trop courte pour que son titre
      // atteigne la ligne des tabs ne deviendrait jamais active. Au fond du
      // scroll c'est forcément elle qu'on regarde, donc elle prime. Le garde
      // `maxScroll > BOTTOM_EPS` évite d'activer la dernière tab quand la
      // liste ne déborde pas du tout (scrollTop 0 = déjà « au fond »).
      const maxScroll = scroller.scrollHeight - scroller.clientHeight;
      if (maxScroll > BOTTOM_EPS && scroller.scrollTop >= maxScroll - BOTTOM_EPS) {
        const lastId = visibleL2[visibleL2.length - 1]?.id;
        if (lastId) {
          setLevel2Id((prev) => (prev === lastId ? prev : lastId));
          return;
        }
      }
      const scrollerTop = scroller.getBoundingClientRect().top;
      let bestId: string | null = null;
      let bestTop = -Infinity;
      for (const [id, section] of sectionRefs.current.entries()) {
        const top = section.getBoundingClientRect().top - scrollerTop;
        // Sections déjà passées ou pile sous la ligne : prend celle dont le
        // top est le plus proche de la ligne (depuis au-dessus).
        if (top <= triggerY && top > bestTop) {
          bestTop = top;
          bestId = id;
        }
      }
      // Fallback : tout en haut, aucune section n'a passé le seuil → prend
      // la première section visible.
      if (!bestId) {
        for (const [id, section] of sectionRefs.current.entries()) {
          const top = section.getBoundingClientRect().top - scrollerTop;
          if (top > 0) { bestId = id; break; }
        }
      }
      if (bestId) setLevel2Id((prev) => (prev === bestId ? prev : bestId));
    };

    compute();
    scroller.addEventListener('scroll', compute, { passive: true });
    return () => scroller.removeEventListener('scroll', compute);
  }, [activeL1?.id, visibleL2]);

  // Auto-centre la tab N2 active dans le strip horizontal. Anim manuelle pour
  // la même raison que smoothScrollTo : `behavior:'smooth'` non fiable ici.
  useEffect(() => {
    if (!level2Id) return;
    const tab = tabL2Refs.current.get(level2Id);
    const strip = tabL2StripRef.current;
    if (!tab || !strip) return;
    const stripRect = strip.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const targetLeft = strip.scrollLeft + (tabRect.left - stripRect.left)
      - (stripRect.width - tabRect.width) / 2;
    const start = strip.scrollLeft;
    const delta = Math.max(0, targetLeft) - start;
    if (Math.abs(delta) < 1) return;
    const t0 = performance.now();
    const step = () => {
      const t = Math.min(1, (performance.now() - t0) / SCROLL_DURATION_MS);
      const eased = 1 - (1 - t) * (1 - t);
      strip.scrollLeft = start + delta * eased;
      if (t < 1) setTimeout(step, 16);
    };
    step();
  }, [level2Id]);

  if (loading) {
    return (
      <div className="carte" ref={carteRef}>
        <div className="carte-header"><h1>La Carte</h1></div>
        <div className="carte-loading">⏳</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="carte" ref={carteRef}>
        <div className="carte-header"><h1>La Carte</h1></div>
        <div className="carte-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Reessayer</button>
        </div>
      </div>
    );
  }

  // Arbre vide : pas seedé. Message simple.
  if (visibleRoots.length === 0) {
    return (
      <div className="carte" ref={carteRef}>
        <div className="carte-header"><h1>La Carte</h1></div>
        <div className="carte-empty-state">
          <p className="carte-empty-title">
            La carte n'est pas encore organisée.
            <br />Demandez au staff de la configurer.
          </p>
        </div>
      </div>
    );
  }

  const renderProductCard = (p: ResolvedProduct, nodeLabel: string) => (
    <CarteProductCard key={p.id} p={p} nodeLabel={nodeLabel} onOpen={setOpened} />
  );

  // Helpers ref-callback : set au mount, delete à l'unmount (React appelle
  // l'ancien ref(null) puis le nouveau ref(el) → la map reste cohérente sans
  // qu'on ait à la reset dans le render body, ce qui ouvrirait une race
  // window où compute() voit une map vide.
  const setSectionRef = (id: string) => (el: HTMLElement | null) => {
    if (el) sectionRefs.current.set(id, el);
    else sectionRefs.current.delete(id);
  };
  const setTabL2Ref = (id: string) => (el: HTMLElement | null) => {
    if (el) tabL2Refs.current.set(id, el);
    else tabL2Refs.current.delete(id);
  };

  return (
    <div className="carte" ref={carteRef}>
      {/* Header + tabs N1/N2 = un seul bloc sticky (top:0). Ombre au scroll. */}
      <div className={`carte-topbar ${scrolled ? 'scrolled' : ''}`} ref={topbarRef}>
      <div className="carte-header">
        <h1>La Carte</h1>
      </div>

      {/* Tabs niveau 1 (roots). Single-root collapse : un arbre seedé n'a qu'une
          racine « Carte », dont la pastille ferait doublon avec le titre
          « La Carte ». Même règle qu'au niveau 2 ci-dessous. activeL1 retombe
          sur visibleRoots[0], donc les sections rendent normalement. */}
      {visibleRoots.length > 1 && (
        <div className="carte-tabs carte-tabs-l1">
          <div className="carte-tabs-scroll">
            {visibleRoots.map((root) => (
              <button
                key={root.id}
                type="button"
                className={`carte-tab carte-tab-l1 ${root.id === activeL1?.id ? 'is-active' : ''}`}
                onClick={() => onPickLevel1(root.id)}
              >
                {root.emoji && <span className="carte-tab-emoji">{root.emoji}</span>}
                {root.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tabs niveau 2 (enfants du root actif). Sticky sous N1. Single-child
          collapse : si le container n'a qu'UN seul sous-nœud, on masque la barre
          de sous-onglets (évite le doublon « La carte du midi » / « Carte du midi »). */}
      {visibleL2.length > 1 && (
        <div className="carte-tabs carte-tabs-l2">
          <div className="carte-tabs-scroll" ref={tabL2StripRef}>
            {visibleL2.map((child) => (
              <button
                key={child.id}
                type="button"
                ref={setTabL2Ref(child.id)}
                className={`carte-tab carte-tab-l2 ${child.id === level2Id ? 'is-active' : ''}`}
                onClick={() => onPickLevel2(child.id)}
              >
                {child.emoji && <span className="carte-tab-emoji">{child.emoji}</span>}
                {child.label}
              </button>
            ))}
          </div>
        </div>
      )}
      </div>{/* /carte-topbar */}

      {/* Sections : une par enfant N2 visible. */}
      <div className="carte-sections">
        {visibleL2.length <= 1 ? (
          // Single-child collapse : container avec 0 ou 1 sous-catégorie → on
          // liste directement TOUS ses produits (DFS) sans sous-onglet ni
          // sous-titre redondant. collectProducts remonte ceux du sous-nœud unique.
          <section className="carte-section">
            <div className="products-list">
              {(() => {
                const products = activeL1 ? collectProducts(activeL1) : [];
                return products.length > 0 ? (
                  products.map((p) => renderProductCard(p, activeL1!.label))
                ) : (
                  <p className="carte-section-empty">Aucun produit pour l'instant.</p>
                );
              })()}
            </div>
          </section>
        ) : (
          visibleL2.map((child) => {
            const products = collectProducts(child);
            return (
              <section
                key={child.id}
                className="carte-section"
                data-node-id={child.id}
                ref={setSectionRef(child.id)}
              >
                <h2 className="carte-section-title">
                  {child.emoji && <span>{child.emoji}</span>} {child.label}
                </h2>
                <div className="products-list">
                  {products.length > 0 ? (
                    products.map((p) => renderProductCard(p, child.label))
                  ) : (
                    <p className="carte-section-empty">Vide pour l'instant.</p>
                  )}
                </div>
              </section>
            );
          })
        )}
      </div>

      <ProductDetailSheet
        product={opened?.product as ViewableProduct | null}
        category={opened?.nodeLabel ?? null}
        onClose={() => setOpened(null)}
      />
    </div>
  );
}
